import { getDefaultBuilderConfig } from '@ctw/shared/api/builder-config/types';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { getBffApiBaseUrl, getStandaloneBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_BUILDER_CONFIG } from '@ctw/shared/utils/query-keys';
import { tw } from '@ctw/shared/utils/tailwind';
import type { BuilderConfig } from '@zeus-health/types';
import { createContext, type PropsWithChildren, useContext } from 'react';

interface BuilderConfigState extends BuilderConfig {}

const BuilderConfigContext = createContext<BuilderConfigState | null>(null);

interface BuilderConfigProviderProps extends PropsWithChildren {}

export const BuilderConfigProvider = ({ children }: BuilderConfigProviderProps) => {
  const hasBffBuilderConfigFeature = useFeatureFlag('ctw-bff-builder-config');

  const builderConfigQuery = useCtwQuery<BuilderConfig>({
    queryId: QUERY_KEY_BUILDER_CONFIG,
    // Include the flag-driven endpoint mode in the query identity so the cached response from one
    // endpoint isn't reused after the flag resolves/changes (the long staleTime below would
    // otherwise pin an initial legacy fetch for the rest of the session).
    queryKey: [hasBffBuilderConfigFeature ? 'bff' : 'legacy'],
    // Cache builder config for longer than other queries to prevent redundant re-fetches on every
    // navigation.
    staleTime: /* 20 minutes */ 1000 * 60 * 20,
    gcTime: /* 20 minutes */ 1000 * 60 * 20,
    queryFn: async ({ requestContext, ctwFetch, telemetry }) => {
      // Gate on the `ctw-bff-builder-config` flag: when enabled we hit the BFF API (which
      // responds with a JSON:API document), otherwise we hit the legacy Next.js endpoint
      // (which responds with the BuilderConfig resource directly).
      const url = hasBffBuilderConfigFeature
        ? `${getBffApiBaseUrl(requestContext.env)}/v1/builders/${requestContext.builderId}/config`
        : `${getStandaloneBaseUrl(requestContext.env)}/api/builders/${requestContext.builderId}/config`;

      const response = await ctwFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${requestContext.authToken}`,
        },
      });

      if (!response.ok && requestContext.serviceEnv !== 'dev') {
        telemetry.trackError({
          message: 'Failed to fetch builder config',
          error: new Error('Failed to fetch builder config'),
          context: {
            responseData: response.data,
          },
        });
      }

      const defaultBuilderConfig = getDefaultBuilderConfig(requestContext.builderId);

      if (!(response.ok && response.data)) {
        return defaultBuilderConfig;
      }

      const returnedBuilderConfig = hasBffBuilderConfigFeature
        ? (response.data as { data?: { attributes?: BuilderConfig } } | null)?.data?.attributes
        : (response.data as BuilderConfig);

      if (!returnedBuilderConfig) {
        return defaultBuilderConfig;
      }

      return {
        ...defaultBuilderConfig,
        ...returnedBuilderConfig,
      } as BuilderConfig;
    },
  });

  if (builderConfigQuery.isError) {
    return (
      <ContentError
        title="Unable to load"
        message="An unknown error occured loading this page."
        className={tw`flex h-full min-h-full w-full min-w-full items-center justify-center`}
      >
        <span>If this problem persists </span>
        <SupportFormLink buttonText="contact support" className={tw`text-base`} />.
      </ContentError>
    );
  }

  if (builderConfigQuery.isLoading || !builderConfigQuery.data) {
    return <LoadingSpinner centered={true} message="Loading..." />;
  }

  return (
    <BuilderConfigContext.Provider value={builderConfigQuery.data}>
      {children}
    </BuilderConfigContext.Provider>
  );
};

export const useBuilderConfig = () => {
  const context = useContext(BuilderConfigContext);

  if (!context) {
    throw new Error('useBuilderConfig must be used within a BuilderConfigProvider');
  }

  return context;
};
